/**
 * ColleagueFace — Face do colega numa figurinha, com precedência foto → avatar → inicial.
 *
 * Espelha a lógica do `UserAvatar` do Dashboard, MAS com fallback gracioso:
 * se a foto (S3) falhar ao carregar (`onError`), cai para o avatar nice-avatar;
 * se não houver avatar, mostra a inicial do nome. Isso evita o círculo vazio quando
 * o colega tem `avatar_type:1` mas a foto não existe/404 (comum entre 23k funcionários).
 *
 * Precedência (igual ao Dashboard quando a foto existe):
 *   - avatar_type falsy (0) + avatar → avatar nice-avatar
 *   - foto disponível (e carrega) → foto
 *   - avatar → avatar nice-avatar
 *   - senão → inicial
 */

import React, { useState } from 'react';
import Avatar from 'funifier-nice-avatar';

interface ColleagueFaceProps {
  /** Foto do colega (player.image) */
  image?: { small?: { url?: string } } | null;
  /** Avatar nice-avatar do colega (player.extra.avatar) */
  avatar?: Record<string, any> | null;
  /** Tipo de avatar (player.extra.avatar_type): 0 = avatar, 1 = foto */
  avatarType?: number;
  /** Inicial do nome (último recurso) */
  initial: string;
  /** Lado do quadrado da face em px */
  size: number;
  /** Classe da inicial (estilo do container) */
  initialClassName?: string;
}

export function ColleagueFace({
  image,
  avatar,
  avatarType,
  initial,
  size,
  initialClassName,
}: ColleagueFaceProps) {
  const [photoFailed, setPhotoFailed] = useState(false);

  const photoUrl = image?.small?.url;
  const realAvatar = avatar && Object.keys(avatar).length ? avatar : null;
  const dim = { width: `${size}px`, height: `${size}px` };

  // avatar_type 0 (ou ausente) prioriza o avatar nice-avatar
  if (!avatarType && realAvatar) {
    return <Avatar style={dim} {...realAvatar} />;
  }

  // foto, se houver e ainda não tiver falhado
  if (photoUrl && !photoFailed) {
    return (
      <img
        src={photoUrl}
        alt=""
        style={{ ...dim, objectFit: 'cover', borderRadius: '50%' }}
        onError={() => setPhotoFailed(true)}
      />
    );
  }

  // fallback: avatar nice-avatar
  if (realAvatar) {
    return <Avatar style={dim} {...realAvatar} />;
  }

  // último recurso: inicial sobre fundo roxo (padrão da casa), preenchendo o círculo
  return (
    <div
      className={initialClassName}
      style={{
        ...dim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#7358c8',
        borderRadius: '50%',
      }}
    >
      {initial}
    </div>
  );
}
